import React, { useEffect } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import Layout from './components/Layout';
import PlatformLayout from './components/platform/PlatformLayout';
import PlatformRoute from './components/PlatformRoute';
import ProtectedRoute from './components/ProtectedRoute';
import { AuthProvider } from './context/AuthContext';
import AgingSection from './pages/AgingSection';
import ContactRequests from './pages/backoffice/ContactRequests';
import Overview from './pages/backoffice/Overview';
import Wineries from './pages/backoffice/Wineries';
import Batches from './pages/Batches';
import Bottling from './pages/Bottling';
import Dashboard from './pages/Dashboard';
import FermentationSection from './pages/FermentationSection';
import InventorySection from './pages/InventorySection';
import HomeRoute from './pages/HomeRoute';
import Login from './pages/Login';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
import Tracking from './pages/Tracking';
import ToolsSection from './pages/ToolsSection';
import UserAdminSection from './pages/UserAdminSection';
import NotificationCenter from './pages/NotificationCenter';
import Notifications from './pages/Notifications';
import Vessels from './pages/Vessels';
import VineyardSection from './pages/VineyardSection';
import WineBlendCalculator from './pages/WineBlendCalculator';
import KmbsCalculator from './pages/KmbsCalculator';
import { enhanceTableScrolling } from './utils/scrollStyles';

/** Preserve query/hash when moving a page under /tools (e.g. Notification Center ?tab=). */
const RedirectWithSearch: React.FC<{ to: string }> = ({ to }) => {
  const { search, hash } = useLocation();
  return <Navigate to={`${to}${search}${hash}`} replace />;
};

const App: React.FC = () => {
  useEffect(() => {
    // Apply custom scrolling enhancements when the app loads
    // and whenever routes change
    const applyScrollingStyles = () => {
      setTimeout(() => {
        enhanceTableScrolling();
      }, 300); // Short delay to ensure DOM is fully rendered
    };

    applyScrollingStyles();

    // Setup an observer to apply styles when route changes
    const observer = new MutationObserver(applyScrollingStyles);
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          {/* Public routes */}
          <Route path="/" element={<HomeRoute />} />
          <Route path="/login" element={<Login />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />

          {/* Protected routes */}
          <Route element={<ProtectedRoute />}>
            <Route element={<Layout />}>
              <Route path="/dashboard" element={<Dashboard />} />

              {/* Vineyard routes */}
              <Route path="/vineyards" element={<VineyardSection />} />
              <Route path="/vineyard-maintenance" element={<Navigate to="/vineyards?tab=maintenance" replace />} />

              {/* Wine Batch routes */}
              <Route path="/batches" element={<Batches />} />

              {/* Fermentation routes */}
              <Route path="/fermentation" element={<FermentationSection />} />
              <Route path="/fermentation-maintenance" element={<Navigate to="/fermentation?tab=maintenance" replace />} />
              <Route path="/post-fermentation" element={<Navigate to="/fermentation?tab=post-fermentation" replace />} />

              {/* Aging routes */}
              <Route path="/aging" element={<AgingSection />} />
              <Route path="/aging-maintenance" element={<Navigate to="/aging?tab=maintenance" replace />} />

              {/* Bottling routes */}
              <Route path="/bottling" element={<Bottling />} />

              {/* Inventory routes */}
              <Route path="/inventory" element={<InventorySection />} />

              {/* Vessel routes */}
              <Route path="/vessels" element={<Vessels />} />

              {/* Tools: tracking, calculators, admin notification center */}
              <Route path="/tools" element={<ToolsSection />}>
                <Route index element={<Navigate to="tracking" replace />} />
                <Route path="tracking" element={<Tracking />} />
                <Route path="blend-calculator" element={<WineBlendCalculator />} />
                <Route path="kmbs-calculator" element={<KmbsCalculator />} />
                <Route path="notification-center" element={<NotificationCenter />} />
              </Route>
              <Route path="/tracking" element={<Navigate to="/tools/tracking" replace />} />
              <Route path="/blend-calculator" element={<Navigate to="/tools/blend-calculator" replace />} />
              <Route path="/kmbs-calculator" element={<Navigate to="/tools/kmbs-calculator" replace />} />
              <Route path="/notification-center" element={<RedirectWithSearch to="/tools/notification-center" />} />

              {/* User management routes */}
              <Route path="/users" element={<UserAdminSection />} />
              <Route path="/roles" element={<Navigate to="/users?tab=roles" replace />} />
              <Route path="/notifications" element={<Notifications />} />
            </Route>
          </Route>

          {/* Platform backoffice */}
          <Route element={<PlatformRoute />}>
            <Route element={<PlatformLayout />}>
              <Route path="/backoffice" element={<Overview />} />
              <Route path="/backoffice/wineries" element={<Wineries />} />
              <Route path="/backoffice/contact-requests" element={<ContactRequests />} />
            </Route>
          </Route>

          {/* Unknown routes → dashboard (ProtectedRoute sends guests to login) */}
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
};

export default App;
